import React, { useEffect, useMemo } from 'react';
import { Modal } from 'react-responsive-modal';
import { useDispatch } from 'react-redux';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { HorizontalCenterWrapper, Text } from '../../v3Styles';
import {
  CartCheckoutButton,
  EmojiBullet,
  OptOutLink,
  PicsPlusAnnualSubBox,
  PicsPlusAnnualSubBoxDate,
  PicsPlusAnnualSubBoxHeader,
  PicsPlusAnnualSubBoxText,
  PicsPlusContainer,
  PicsPlusHeader,
  PicsPlusHeader2,
  PicsPlusListItem,
  PicsPlusListItemHeader,
  PicsPlusText,
  SecondaryButton,
} from './picsPlusModalStyles';
import { setAcceptedPicsPlus } from '../../../../actions/v3Order';

// Default content used when no experiment is active
const DEFAULT_CONTENT = {
  headline: '✨ Pics+',
  subheadline: 'Save more. Stress less. Love it all.',
  cta_text: '✨ Start My FREE Trial',
  decline_text: 'No thanks, continue without Pics+',
  trial_box_headline: '🎉 Try FREE for 3 Months!',
  trial_box_subtext: 'No payment today • Cancel anytime',
  price_display: '$2.99/year',
  after_trial_text: 'After trial • Just $0.25/month • Save $30+ over 3 years',
  benefits: [
    {
      emoji: '📸',
      title: 'Your memories, saved forever',
      description:
        'No expiration dates, no surprises. Your precious moments stay safe and accessible for life.',
    },
    {
      emoji: '💰',
      title: 'Save on every order - zero fees',
      description:
        'No service fees, ever. Keep more money in your pocket with every single order you place.',
    },
    {
      emoji: '🏠',
      title: 'One membership, unlimited coverage',
      description:
        'Perfect for growing families! A single subscription covers everyone in your household.',
    },
    {
      emoji: '🎁',
      title: 'Exclusive member-only perks',
      description:
        'Early access to new products, special offers, and surprise member treats throughout the year.',
    },
    {
      emoji: '💚',
      title: 'Help families in need',
      description:
        'Every membership supports scholarships so all children can enjoy their school portrait experience.',
    },
  ],
};

const PicsPlusModal = ({
  open,
  onClose,
  onDecline,
  onSubmit,
  subscriberCount = 0,
  experimentConfig = null,
}) => {
  const dispatch = useDispatch();

  // Merge experiment content with defaults
  const content = useMemo(() => {
    if (!experimentConfig?.content) {
      return DEFAULT_CONTENT;
    }
    return {
      ...DEFAULT_CONTENT,
      ...experimentConfig.content,
      benefits: experimentConfig.content.benefits || DEFAULT_CONTENT.benefits,
    };
  }, [experimentConfig]);

  // Calculate price display from experiment config
  const priceDisplay = useMemo(() => {
    if (experimentConfig?.price_cents) {
      const dollars = (experimentConfig.price_cents / 100).toFixed(2);
      return `$${dollars}/year`;
    }
    return content.price_display;
  }, [experimentConfig, content.price_display]);

  // Format trial end date for display
  const trialEndDate = useMemo(() => {
    if (experimentConfig?.trial_end_date) {
      const date = new Date(experimentConfig.trial_end_date);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }
    // Default to 90 days from now
    const date = new Date();
    date.setDate(date.getDate() + 90);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }, [experimentConfig]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const modalElement = document.querySelector('.pics-plus-modal');
        const overlayElement = document.querySelector('.overlay');
        if (overlayElement) {
          overlayElement.scrollTop = 0;
        }
        if (modalElement) {
          modalElement.scrollTop = 0;
        }
        window.scrollTo(0, 0);
      }, 100);
    }
  }, [open]);

  const handleSubmit = () => {
    onSubmit();
    dispatch(setAcceptedPicsPlus(true));
  };

  const handleClose = () => {
    onClose();
  };

  const handleDecline = () => {
    if (onDecline) {
      onDecline();
    } else {
      onClose();
    }
    dispatch(setAcceptedPicsPlus(false));
  };

  // Format subscriber count for display
  const formatSubscriberCount = (count) => {
    if (count === 0) return 'Be among the first';
    if (count < 10) return 'Join our founding members';
    if (count < 100) return `Join ${count}+ families`;
    // Round to nearest 10 for social proof
    const rounded = Math.floor(count / 10) * 10;
    return `Join ${rounded.toLocaleString()}+ families`;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeIcon={
        <XMarkIcon
          style={{
            width: '28px',
            height: '28px',
            color: '#999',
            cursor: 'pointer',
          }}
        />
      }
      classNames={{
        root: 'modal-root',
        overlay: 'overlay',
        modal: 'pics-plus-modal',
      }}
      center={false}
      styles={{
        overlay: {
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '0',
          overflowY: 'auto',
        },
        modal: {
          margin: '20px auto',
          padding: 0,
          maxHeight: 'calc(100vh - 40px)',
          overflowY: 'auto',
          width: '100%',
          maxWidth: '600px',
        },
      }}
    >
      <PicsPlusContainer>
        <PicsPlusHeader>{content.headline}</PicsPlusHeader>
        <PicsPlusHeader2>{content.subheadline}</PicsPlusHeader2>
        <Text
          style={{
            fontWeight: 600,
            fontSize: '14px',
            color: '#999',
            marginBottom: '32px',
          }}
        >
          {formatSubscriberCount(subscriberCount)} who trust Pics+ ❤️
        </Text>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {content.benefits.map((benefit, index) => (
            <PicsPlusListItem key={index}>
              <EmojiBullet>{benefit.emoji}</EmojiBullet>
              <div>
                <PicsPlusListItemHeader>{benefit.title}</PicsPlusListItemHeader>
                <PicsPlusText>{benefit.description}</PicsPlusText>
              </div>
            </PicsPlusListItem>
          ))}
        </ul>
        <PicsPlusAnnualSubBox>
          <PicsPlusAnnualSubBoxHeader style={{ fontSize: '18px' }}>
            {content.trial_box_headline}
          </PicsPlusAnnualSubBoxHeader>
          <PicsPlusAnnualSubBoxDate>
            Free until {trialEndDate} • Cancel anytime
          </PicsPlusAnnualSubBoxDate>
          <PicsPlusHeader style={{ marginTop: '10px' }}>
            {priceDisplay}
          </PicsPlusHeader>
          <PicsPlusAnnualSubBoxText>
            {content.after_trial_text}
          </PicsPlusAnnualSubBoxText>
          <Text
            style={{
              fontSize: '12px',
              color: '#888',
              marginTop: '8px',
              fontStyle: 'italic',
            }}
          >
            We&apos;ll email you 7 days before your trial ends
          </Text>
        </PicsPlusAnnualSubBox>
        <CartCheckoutButton onClick={handleSubmit} id="proceed-to-checkout">
          {content.cta_text}
        </CartCheckoutButton>
        <HorizontalCenterWrapper style={{ marginTop: '12px' }}>
          <SecondaryButton onClick={handleDecline}>
            {content.decline_text}
          </SecondaryButton>
        </HorizontalCenterWrapper>
      </PicsPlusContainer>
    </Modal>
  );
};

export default PicsPlusModal;
